import { FC } from 'react';
import classNames from 'classnames';
import styles from './filter-cards.module.scss';
import { formatNumber } from 'utils/number';

export type FilterCardId =
  | 'total'
  | 'selected'
  | 'wellsNotSelected'
  | 'wellsMissingForecast'
  | 'wellsInEarlyTime'
  | 'wellsUpdated';

interface FilterCard {
  id: FilterCardId;
  label: string;
}

const CARDS: FilterCard[] = [
  { id: 'total', label: 'Total Number of Wells' },
  { id: 'selected', label: 'Number of Wells Selected' },
  { id: 'wellsNotSelected', label: 'Number of Wells Not Selected' },
  { id: 'wellsMissingForecast', label: 'Number of Wells Missing DCA Forecast' },
  { id: 'wellsInEarlyTime', label: 'Number of Early Time Wells' },
  { id: 'wellsUpdated', label: 'Wells Updated / Staged For Promotion' },
];

/** Cards whose count is rendered in the highlight color (greenish). */
const HIGHLIGHTED: ReadonlySet<FilterCardId> = new Set<FilterCardId>([
  'wellsMissingForecast',
  'wellsInEarlyTime',
  'wellsUpdated',
]);

interface FilterCardsProps {
  activeFilters: Set<FilterCardId>;
  onFilterClick: (id: FilterCardId) => void;
  counts: Partial<Record<FilterCardId, number | null>>;
}

export const FilterCards: FC<FilterCardsProps> = ({
  activeFilters,
  onFilterClick,
  counts,
}) => (
  <div className={styles.container}>
    {CARDS.map(({ id, label }) => {
      const isDisabled = id !== 'total' && (counts[id] ?? 0) === 0;
      const isActive = activeFilters.has(id);
      return (
        <button
          key={id}
          type="button"
          aria-pressed={isActive}
          className={classNames(styles.card, {
            [styles.active]: isActive,
            [styles.disabled]: isDisabled,
          })}
          disabled={isDisabled}
          onClick={() => onFilterClick(id)}
        >
          <div className={styles.label}>{label}</div>
          <div
            className={classNames(styles.value, {
              [styles.valueHighlight]: HIGHLIGHTED.has(id),
            })}
          >
            {counts[id] !== null
              ? formatNumber({
                  value: counts[id]!,
                  minPrecision: 0,
                  maxPrecision: 0,
                })
              : '--'}
          </div>
        </button>
      );
    })}
  </div>
);
